<template>
  <form novalidate @submit.prevent="submitQuote">
    <LsSelect
      v-model="bushes_0ft_5ft_count"
      :options="options"
      label="How many shrubs and hedges that are less than 5 ft tall need to be trimmed?"
      track-id="instant_quote_bush_trimming_form-bushes_0ft_5ft_count"
      name="bushes_0ft_5ft_count"
      test-id="bushes_0ft_5ft_count"
      :disabled="Boolean(instantQuote.currentQuote)"
      :validation="{ required: true, numeric: true }"
    />
    <LsSelect
      v-model="bushes_5ft_10ft_count"
      :options="options"
      label="How many shrubs and hedges that are between 5 ft to 10 ft tall need to be trimmed?"
      collapse-content="Medium price impact on total quote amount. Trimming shrubs or hedges of this size might require specialized tools."
      track-id="instant_quote_bush_trimming_form-bushes_5ft_10ft_count"
      name="bushes_5ft_10ft_count"
      test-id="bushes_5ft_10ft_count"
      :disabled="Boolean(instantQuote.currentQuote)"
      :validation="{ required: true, numeric: true }"
    />
    <LsSelect
      v-model="bushes_over_10ft_count"
      :options="options"
      label="How many shrubs and hedges that are more than 10 ft tall need to be trimmed?"
      collapse-content="High price impact on total quote amount. Trimming shrubs or hedges of this size might require a significant amount of time and specialized tools."
      track-id="instant_quote_bush_trimming_form-bushes_over_10ft_count"
      name="bushes_over_10ft_count"
      test-id="bushes_over_10ft_count"
      :disabled="Boolean(instantQuote.currentQuote)"
      :validation="{ required: true, numeric: true }"
    />
    <LsRadios
      v-model="desired_clippings_haulaway"
      :options="[{ label: 'Yes', value: true }, { label: 'No', value: false }]"
      name="desired_clippings_haulaway"
      :validation="{ required: true }"
      label="Would you like the provider to bag and haul off the clippings?"
      collapse-content="Medium price impact on total quote amount as the provider has to haul the clippings to a specific location and may have to pay a disposal fee."
      track-id="instant_quote_bush_trimming_form-desired_clippings_haulaway"
      :disabled="Boolean(instantQuote.currentQuote)"
    />
    <LsCheckboxes
      v-model="locations"
      :options="InstantQuoteBushTrimmingConstants.locations"
      label="Where on the property? (From the street)"
      track-id="instant_quote_bush_trimming_form-locations"
      name="locations"
      test-id="locations"
      :validation="{ required: true }"
      :disabled="Boolean(instantQuote.currentQuote)"
    />
    <LsRadios
      v-if="showCycleOptions"
      v-model="cycle"
      :options="InstantQuoteConstants.cycleOptions"
      name="cycle"
      :validation="{ required: true }"
      label="How frequently would you like to get your shrubs and hedges trimmed?"
      track-id="instant_quote_bush_trimming_form-cycle"
      :disabled="Boolean(instantQuote.currentQuote)"
    />
    <LsInput
      v-model="details"
      track-id="instant_quote_bush_trimming_form-details"
      test-id="details"
      label="Any other details you would like your lawn care pro to know?"
      name="details"
      type="textarea"
      placeholder="Add as much detail as possible so that the provider can complete the job as per your expectations."
      :disabled="Boolean(instantQuote.currentQuote)"
    />
    <LsUploadPhotos
      v-model="images"
      track-id="instant_quote_bush_trimming_form-images"
      test-id="images"
      label="Upload photos"
      collapse-content="Upload photos so that your provider knows what shrubs/hedges need to be trimmed and what tools they would need to complete the job."
      name="images"
      :disabled="Boolean(instantQuote.currentQuote)"
    />

    <div v-if="!instantQuote.currentQuote">
      <LsButton
        text="generate quote"
        track-id="instant_quote_bush_trimming_form-generate_quote"
        test-id="generate_quote"
        type="submit"
        :loading="instantQuote.loadingStatus.isUpdating"
      />
    </div>

    <InstantQuoteFormGeneratedQuote v-else :property_id="property_id" />
  </form>
</template>

<script>
import _ from 'lodash';
import {
  LsRadios,
  LsButton,
  LsInput,
  LsUploadPhotos,
  LsCheckboxes,
  LsSelect,
} from '@/components/common';
import { mapActions, mapState, mapGetters } from 'vuex';
import { lsRouterHelpers, lsValidationService } from 'ls-vue-common';
import { InstantQuoteConstants, InstantQuoteBushTrimmingConstants } from 'ls-constants';
import {
  INSTANT_QUOTE__GENERATE_QUOTE,
  INSTANT_QUOTE__RESET_QUOTE,
  INSTANT_QUOTE__SUGGESTED_JOB_CLICK,
  PROPERTIES__SELECTED_PROPERTY,
} from '@/store/types';
import InstantQuoteFormGeneratedQuote from '../components/InstantQuoteFormGeneratedQuote.vue';
import { lsInstantQuoteFormHelpers, lsUploadPhotosHelper } from '@/helpers';

export default {
  name: 'InstantQuoteBushTrimmingForm',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    LsButton,
    LsRadios,
    LsInput,
    LsUploadPhotos,
    LsCheckboxes,
    LsSelect,
    InstantQuoteFormGeneratedQuote,
  },
  data() {
    const { cycleOptions } = InstantQuoteConstants;
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      instant_quote_suggestion_id: lsRouterHelpers.getParamOrQuery(
        this.$route,
        'instant_quote_suggestion_id'
      ),
      // form
      bushes_0ft_5ft_count: null,
      bushes_5ft_10ft_count: null,
      bushes_over_10ft_count: null,
      cycle: lsInstantQuoteFormHelpers.getDefaultCycleValue({ cycleOptions }),
      desired_trim_length: 'neat',
      details: '',
      desired_clippings_haulaway: false,
      images: [],
      locations: [],
    };
  },
  computed: {
    ...mapState(['instantQuote', 'featureFlags']),
    ...mapGetters([PROPERTIES__SELECTED_PROPERTY]),
    InstantQuoteConstants() {
      return _.cloneDeep(InstantQuoteConstants);
    },
    InstantQuoteBushTrimmingConstants() {
      return _.cloneDeep(InstantQuoteBushTrimmingConstants);
    },
    options() {
      return _.map(_.range(31), (num) => {
        return { label: _.toString(num), value: num };
      });
    },
    selectedProperty() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id);
    },
    suggestion() {
      return _.find(this.selectedProperty.instant_quote_suggestions, {
        id: _.toNumber(this.instant_quote_suggestion_id),
      });
    },
    showCycleOptions() {
      return _.get(this.featureFlags.flags, ['customer.instant_quotes.frequency_options']);
    },
  },
  created() {
    this[INSTANT_QUOTE__RESET_QUOTE]();
    if (this.instant_quote_suggestion_id) {
      const { instant_quote_bush_trimming } = this.suggestion;

      this.bushes_0ft_5ft_count = _.get(instant_quote_bush_trimming, 'bushes_0ft_5ft_count', null);
      this.bushes_5ft_10ft_count = _.get(
        instant_quote_bush_trimming,
        'bushes_5ft_10ft_count',
        null
      );
      this.bushes_over_10ft_count = _.get(
        instant_quote_bush_trimming,
        'bushes_over_10ft_count',
        null
      );
      this.desired_clippings_haulaway = _.get(
        instant_quote_bush_trimming,
        'desired_clippings_haulaway',
        false
      );
      this[INSTANT_QUOTE__SUGGESTED_JOB_CLICK]({
        property_id: this.property_id,
        instant_quote_suggestion_id: this.instant_quote_suggestion_id,
      });
    }
  },
  methods: {
    ...mapActions([
      INSTANT_QUOTE__GENERATE_QUOTE,
      INSTANT_QUOTE__RESET_QUOTE,
      INSTANT_QUOTE__SUGGESTED_JOB_CLICK,
    ]),
    async submitQuote() {
      const isFormValid = await this.$validator.validateAll();
      const isUploadValid = lsUploadPhotosHelper.isValidUpload(this.images);

      if (!isFormValid || !isUploadValid) {
        return lsValidationService.scrollToInvalid();
      }
      await this[INSTANT_QUOTE__GENERATE_QUOTE]({
        property_id: this.property_id,
        type: 'instant_quote_bush_trimming',
        formData: {
          bushes_0ft_5ft_count: this.bushes_0ft_5ft_count,
          bushes_5ft_10ft_count: this.bushes_5ft_10ft_count,
          bushes_over_10ft_count: this.bushes_over_10ft_count,
          cycle: this.cycle,
          desired_trim_length: this.desired_trim_length,
          details: this.details ? this.details : null,
          desired_clippings_haulaway: this.desired_clippings_haulaway,
          images: this.images,
          locations: this.locations,
          instant_quote_suggestion_id: this.instant_quote_suggestion_id || null,
          service_promo_id: _.toNumber(lsRouterHelpers.getParam(this.$route, 'service_promo_id')),
        },
      });

      lsInstantQuoteFormHelpers.scrollToBottom({ document });
    },
  },
};
</script>

<style lang="scss" scoped>
form > :not(:last-child) {
  margin-bottom: 20px !important;
}
</style>
